import { Component, inject } from '@angular/core';
import { fadeAnimation } from '../../../core/animations/fade.animation';
import { LocalizedUrlService } from '../../../core/services/localized-url.service';

@Component({
  selector: 'app-home-page',
  animations: [fadeAnimation],
  template: `
    <app-page-section>
      <div class="flex lg:gap-20">
        <div class="w-full flex-none md:w-[60%] lg:w-1/2">
          <h1
            >{{ 'home.hero.title' | transloco }} <br class="hidden lg:inline" /></h1
          >
          <p class="mt-6 text-balance"
            >{{ 'home.hero.description' | transloco }}</p
          >
          <div class="mt-6 flex flex-wrap gap-5 xl:mt-10">
            <app-button variant="primary" [routerLink]="localizedUrl.link('becomeHost')"
              >{{ 'home.hero.cta-host' | transloco }}</app-button
            >
            <app-button
              variant="outlined"
              [routerLink]="localizedUrl.link('about')"
              >{{ 'home.hero.cta-discover' | transloco }}</app-button
            >
          </div>
        </div>
        <div class="hidden flex-none grow md:block">
          <div class="-mt-20 w-full overflow-hidden md:w-[900px]">
            <img
              class="w-full"
              ngSrc="header.webp"
              priority
              width="1000"
              height="669"
              sizes="50vw"
              [attr.alt]="'home.hero.img-alt' | transloco"
              [attr.title]="'home.hero.img-alt' | transloco"
            />
          </div>
        </div>
      </div>
    </app-page-section>

    <app-page-section
      [noContainer]="true"
      [waves]="true"
      backgroundColor="secondary"
      offsetImage="cactus.png"
      id="features"
    >
      <div class="container">
        <div
          class="flex flex-col items-center justify-center space-y-2 text-white"
        >
          <div class="flex items-center justify-center gap-3 text-white/40">
            <span class="text-2xl font-light">/</span>
            <i class="fal fa-rocket text-white text-2xl"></i>
            <span class="text-2xl font-light">/</span>
          </div>
          <h2>{{ 'home.features.title' | transloco }}</h2>
        </div>
        <div
          class="mt-20 flex flex-col flex-wrap justify-center gap-x-16 gap-y-20 lg:flex-row"
        >
          <app-feature
            img="/assets/img/features/location-equipements.svg"
            [title]="'home.features.equipment-rental.title' | transloco"
            [description]="'home.features.equipment-rental.description' | transloco"
          ></app-feature>

          <app-feature
            img="/assets/img/features/paiement-en-ligne.svg"
            [title]="'home.features.automatic-billing.title' | transloco"
            [description]="'home.features.automatic-billing.description' | transloco"
          ></app-feature>

          <app-feature
            img="/assets/img/features/reservation-en-ligne.svg"
            [title]="'home.features.booking-management.title' | transloco"
            [description]="'home.features.booking-management.description' | transloco"
          ></app-feature>

          <app-feature
            img="/assets/img/features/automatisation-conditions-annulation.svg"
            [title]="'home.features.autonomous-cancellation.title' | transloco"
            [description]="'home.features.autonomous-cancellation.description' | transloco"
          ></app-feature>

          <app-feature
            img="/assets/img/features/gestion-utilisateurs.svg"
            [title]="'home.features.internal-external.title' | transloco"
            [description]="'home.features.internal-external.description' | transloco"
          ></app-feature>

          <app-feature
            img="/assets/img/features/gestion-groupes-tarifaires.svg"
            [title]="'home.features.discount-vouchers.title' | transloco"
            [description]="'home.features.discount-vouchers.description' | transloco"
          ></app-feature>

          <app-feature
            img="/assets/img/features/dormakaba.svg"
            [title]="'home.features.dormakaba.title' | transloco"
            [description]="'home.features.dormakaba.description' | transloco"
            logo="features/logo-dormakaba.png"
          ></app-feature>

          <app-feature
            img="/assets/img/features/gestion-salles.svg"
            [title]="'home.features.room-groups.title' | transloco"
            [description]="'home.features.room-groups.description' | transloco"
          ></app-feature>
        </div>
      </div>
    </app-page-section>

    <app-page-section padding="lg">
      <div class="grid items-center gap-8 md:grid-cols-2 md:gap-16">
        <div class="order-1 col-span-1 md:order-0 md:flex md:justify-end">
          <img
            class="max-h-[500px] w-auto"
            ngSrc="keyboard.webp"
            width="1076"
            height="1070"
            sizes="(max-width: 768px) 80vw, 25vw"
            [attr.alt]="'home.about.img-alt' | transloco"
          />
        </div>
        <div class="order-0 col-span-1 md:order-1">
          <div class="flex items-center gap-3 text-secondary-900/40">
            <span class="text-2xl font-light">/</span>
            <i class="fal fa-magnifying-glass text-secondary-900 text-2xl"></i>
            <span class="text-2xl font-light">/</span>
          </div>
          <h2 class="mt-5" [innerHTML]="'home.about.title' | transloco"></h2>
          <p class="mt-4"
            >{{ 'home.about.description' | transloco }}</p
          >
          <div class="mt-8">
            <app-button [routerLink]="localizedUrl.link('about')" icon="arrow-right"
              >{{ 'home.about.cta' | transloco }}</app-button
            >
          </div>
        </div>
      </div>
    </app-page-section>

    <app-page-section
      backgroundColor="neutral"
      [noContainer]="true"
      padding="lg"
    >
      <div class="mx-auto justify-center text-center md:w-[400px]">
        <div class="relative pt-16 md:pt-0">
          <img
            class="absolute -top-40 left-4 w-64 md:-top-72 md:-left-72 md:w-96"
            ngSrc="coffee-clipboard.png"
            width="1002"
            height="939"
            [attr.alt]="'home.collab.img-alt' | transloco"
            [attr.title]="'home.collab.img-alt' | transloco"
          />

          <div class="mt-10 mr-10 text-right md:mt-0 md:mr-3">
            <div class="mb-4 flex items-center justify-end gap-3 text-secondary-900/40">
              <span class="text-2xl font-light">/</span>
              <i class="fal fa-bullseye-arrow text-secondary-900 text-2xl"></i>
              <span class="text-2xl font-light">/</span>
            </div>
            <div class="text-right text-2xl font-bold"
              >{{ 'home.collab.title' | transloco }}</div
            >
            <div class="mt-4">
              <app-public-tag
                >{{ 'home.collab.tag' | transloco }}</app-public-tag
              >
            </div>
          </div>
        </div>
      </div>
    </app-page-section>
  `,
  standalone: false,
})
export class HomePageComponent {
  protected localizedUrl = inject(LocalizedUrlService);
}
